/** Geodesy helpers on a spherical earth (sufficient for panorama work at < 0.1° error). */

export const EARTH_RADIUS = 6371008.8; // mean earth radius in metres
export const DEG = Math.PI / 180;
export const RAD = 180 / Math.PI;

/** Standard atmospheric refraction coefficient used by surveyors. */
export const DEFAULT_REFRACTION = 0.13;

export interface LatLon {
  lat: number;
  lon: number;
}

export interface Observer extends LatLon {
  /** Eye elevation above sea level in metres. */
  elevation: number;
}

export function normalizeAzimuth(deg: number): number {
  const a = deg % 360;
  return a < 0 ? a + 360 : a;
}

/** Signed difference a - b wrapped into (-180, 180]. */
export function azimuthDelta(a: number, b: number): number {
  let d = (a - b) % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

/** Great-circle distance in metres. */
export function distance(a: LatLon, b: LatLon): number {
  const φ1 = a.lat * DEG;
  const φ2 = b.lat * DEG;
  const dφ = (b.lat - a.lat) * DEG;
  const dλ = (b.lon - a.lon) * DEG;
  const s = Math.sin(dφ / 2) ** 2 + Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * EARTH_RADIUS * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** Initial bearing from a to b in degrees, 0..360 clockwise from north. */
export function bearing(a: LatLon, b: LatLon): number {
  const φ1 = a.lat * DEG;
  const φ2 = b.lat * DEG;
  const dλ = (b.lon - a.lon) * DEG;
  const y = Math.sin(dλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ);
  return normalizeAzimuth(Math.atan2(y, x) * RAD);
}

/** Point reached after travelling `distanceM` metres from `origin` along `bearingDeg`. */
export function destination(origin: LatLon, bearingDeg: number, distanceM: number): LatLon {
  const δ = distanceM / EARTH_RADIUS;
  const θ = bearingDeg * DEG;
  const φ1 = origin.lat * DEG;
  const λ1 = origin.lon * DEG;
  const sinφ1 = Math.sin(φ1);
  const cosφ1 = Math.cos(φ1);
  const sinδ = Math.sin(δ);
  const cosδ = Math.cos(δ);
  const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * sinδ * cosφ1, cosδ - sinφ1 * sinφ2);
  return { lat: φ2 * RAD, lon: ((λ2 * RAD + 540) % 360) - 180 };
}

/**
 * Apparent drop of a point at horizontal distance `d` below the observer's
 * horizontal plane, accounting for earth curvature and atmospheric refraction.
 */
export function curvatureDrop(d: number, refraction = DEFAULT_REFRACTION): number {
  return (d * d * (1 - refraction)) / (2 * EARTH_RADIUS);
}

/** Elevation angle in degrees of a point `dh` metres above the observer at distance `d`. */
export function elevationAngle(dh: number, d: number, refraction = DEFAULT_REFRACTION): number {
  return Math.atan2(dh - curvatureDrop(d, refraction), d) * RAD;
}

/** Distance in metres to the geometric horizon for an eye height `h` metres above a flat sea. */
export function horizonDistance(h: number, refraction = DEFAULT_REFRACTION): number {
  return Math.sqrt((2 * EARTH_RADIUS * Math.max(h, 0)) / (1 - refraction));
}

export function formatDistance(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  if (m < 10000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m / 1000)} km`;
}

export function formatElevation(m: number): string {
  return `${Math.round(m).toLocaleString("en-US").replace(/,/g, " ")} m`;
}

const COMPASS = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"];
export function compassPoint(azimuth: number): string {
  return COMPASS[Math.round(normalizeAzimuth(azimuth) / 22.5) % 16];
}
